import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from "frontend-components";
import { driversQueryOptions, taxonomiesQueryOptions } from "@pulse/api/queryApi";
import type { Driver } from "@pulse/api/driverApi";
import type { Taxonomy as TaxonomyApi } from "@pulse/api/taxonomyApi";
import { useFieldContext } from "./Form";
import { useDriverTitle, useTaxonomyTitle } from "@pulse/hooks/taxiDriverHooks";
import { useTranslation } from "react-i18next";

export function Taxonomy() {
    const { t } = useTranslation();
    const field = useFieldContext();
    const { data: drivers } = useQuery(driversQueryOptions());
    const { data: taxonomies } = useQuery(taxonomiesQueryOptions());
    const [taxonomy, setTaxonomy] = useState<number | null>(null);
    const driverTitleTFn = useDriverTitle();
    const taxonomyTitleTFn = useTaxonomyTitle();

    useEffect(() => {
        const selectedDriver = (drivers ?? []).find(
            (driver: Driver) => driver.id === (field.state.value as number),
        );
        setTaxonomy(selectedDriver?.taxonomyId ?? null);
    }, [drivers, field.state.value]);

    const taxonomyList = taxonomies ?? [];
    const filteredDrivers = (drivers ?? []).filter(
        (driver: Driver) => driver.taxonomyId === taxonomy,
    );

    return (
        <div className="flex flex-wrap items-start gap-2 w-full min-w-0">
            <Combobox
                items={taxonomyList.map((item: TaxonomyApi) => item.id)}
                value={taxonomy}
                onValueChange={(value: number | null) => {
                    if (value === null) {
                        field.handleChange(null);
                    }
                    setTaxonomy(value);
                }}
                itemToStringLabel={(value: number) => {
                    const selected = taxonomyList.find((item: TaxonomyApi) => item.id === value);
                    return selected ? taxonomyTitleTFn(selected) : "";
                }}
            >
                <ComboboxInput
                    className="w-auto max-w-full bg-white [&_input]:min-w-0 [&_input]:flex-initial [&_input]:field-sizing-content"
                    placeholder={t("question.selectTaxonomy")}
                    showClear
                />
                <ComboboxContent>
                    <ComboboxEmpty>{t("common.noOptions")}</ComboboxEmpty>
                    <ComboboxList>
                        {taxonomyList.map((item: TaxonomyApi) => (
                            <ComboboxItem key={item.id} value={item.id}>
                                <span className="min-w-0 flex-1 wrap-break-word">
                                    {taxonomyTitleTFn(item)}
                                </span>
                            </ComboboxItem>
                        ))}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
            <Combobox
                items={filteredDrivers.map((driver: Driver) => driver.id)}
                value={field.state.value as number}
                onValueChange={(value: number | null) => field.handleChange(value)}
                itemToStringLabel={(value: number) => {
                    const selected = (drivers ?? []).find((driver: Driver) => driver.id === value);
                    return selected ? driverTitleTFn(selected) : "";
                }}
            >
                <ComboboxInput
                    className="w-full max-w-full bg-white [&_input]:min-w-0 [&_input]:flex-initial [&_input]:field-sizing-content"
                    placeholder={t("question.selectDriver")}
                    showClear
                    onBlur={field.handleBlur}
                />
                <ComboboxContent>
                    <ComboboxEmpty>{t("question.driverNotSelected")}</ComboboxEmpty>
                    <ComboboxList>
                        {filteredDrivers.map((driver: Driver) => (
                            <ComboboxItem key={driver.id} value={driver.id}>
                                <span className="min-w-0 flex-1 wrap-break-word">
                                    {driverTitleTFn(driver)}
                                </span>
                            </ComboboxItem>
                        ))}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
        </div>
    );
}
